// Required core libs
import $ from 'cash-dom';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
// Third party libs

//Styled components and css
import BayLaunchpad from '../BayLaunchpad';
import BayNavDrawer from '../BayNavDrawer';
import BayUserTour, { type UserTourConfig } from '../BayUserTour';
import type { TranslationFunction } from '../types/common';

import ActAsBanner from './components/ActAsBanner';
import ActAsModal from './components/ActAsModal';
import isActingAsAnotherUser from './components/isActingAsAnotherUser';
import logOff, { canLogOff } from './components/logOff';

//TODO: Refactor this scss away
import './styles/myaccount.scss';
import './styles/notifications.scss';
import './styles/zoho.scss';

// The child components...
import BalanceDueBanner from './components/BalanceDueBanner';
import DesktopBranding from './components/DesktopBranding';
import FeatureMenuButton from './components/FeatureMenuButton';
import FreeTrialBanner from './components/FreeTrialBanner';
import LaunchpadButton from './components/LaunchpadButton';
import MyAccountButton from './components/MyAccountButton';
import NotifyButton from './components/NotifyButton';
import QuickLinksButton from './components/QuickLinksButton';
import SettingsButton from './components/SettingsButton';
import SupportButton from './components/SupportButton';
import {
	ActionNav,
	BayNav,
	DividerDiv,
	LaunchpadButtonWrapper,
} from './styles/navStyles';

// Bay UI components

interface User {
	ActingAsPUBLICID: string;
	PUBLICID: string;
	ActingAsFIRSTNAME?: string;
	ActingAsLASTNAME?: string;
	ActingAsIMAGEPATH?: string;
	ActingAsPLATFORMLOGOPATH?: string;
	ActingAsEMAIL?: string;
	ActingAsWEBSITEADDRESS?: string;
	ActingAsACCESSLEVEL?: string;
	ActingAsSource?: string;
	ActingAsActorAccount?: boolean;
	isFreeTrial?: boolean;
	daysLeft?: number;
	expiredFreeTrial?: boolean;
	billingStatus?: number;
	outstandingBalAlert?: string;
	outstandingBalAmt?: string;
	outstandingBalPayNowLink?: boolean;
	isEnterprise?: boolean;
	agent_charges_to_parent_account?: boolean;
	totalCredits?: number;
	// Drives the required Act As gate below. Optional, so a host that omits it
	// gets no gate -- the server is what enforces the rule; this only keeps a
	// global admin from sitting in an un-scoped UI.
	IsGlobalAdmin?: boolean;
}

interface NotificationData {
	id?: string;
	message?: string;
	[key: string]: any;
}

interface TopNavProps {
	t?: TranslationFunction;
	user: User;
	appOrigin?: string;
	showAnnouncementBanners?: boolean;
	sharedAppOrigin?: string;
	fetchNotificationsFunc?: () => void;
	fetchQuickLinksFunc?: () => void;
	showNotifications?: boolean;
	notificationsData?: NotificationData[];
	quickLinksData?: Record<string, any>;
	bodyId?: string;
	withBanners?: 'none' | 'single' | 'double';
	reqAccess: Record<string, any>;
	platformType: 'client' | 'social' | string;
	settingsOptions?: Record<string, any>;
	showFeatureButton?: string | boolean;
	featureList?: Record<string, any>;
	setFeatureFunc?: (feature: any) => void;
	children?: ReactNode;
	productStages?: Record<string, any>;
	openMenuDrawer?: string;
	setIsDrawerOpen: (isOpen: boolean) => void;
	enableTestimonialTree?: boolean;
	quickLinkService?: any;
	showSupportButton?: boolean;
	currentLanguage?: string;
	onLanguageChange?: (langCode: string) => void;
	supportedLanguages?: string[];
	weglotReady?: boolean;
	enableTranslations?: boolean;
	showLaunchpadButton?: boolean;
	/** Render the Back At You wordmark at the left of the bar (≥768px) */
	showBrandLogo?: boolean;
	showLaunchpadUpgradeButton?: boolean;
	menuType?: string;
	setMenuType?: (type: string) => void;
	showOffCanvas?: boolean;
	setShowOffCanvas?: (show: boolean) => void;
	toggleDrawer?: (type: string, open?: boolean) => void;
	updateNavSettings?: (settings: any) => void;
	showApiTokenButton?: boolean;
	/**
	 * Signs the user out. The required Act As gate offers this as its only exit
	 * that is not an impersonation, so a host on a platform this library has no
	 * built-in path for must supply it or its global admins have no way out.
	 */
	onLogout?: () => void;
	isMobileView?: boolean;
	navSettings?: Record<string, any>;
	readLaunchpadApi?: () => void;
	updateLaunchpadApi?: any;
	fetchWebsiteDetailsApi?: () => void;
	selectLaunchpadSettingsData?: any;
	selectLaunchpadStatus?: any;
	selectUserProductAccess?: any;
	setLaunchpadSettingsData?: (data: any) => void;
	setUserProductAccess?: (access: any) => void;
	setWebsiteDetails?: (details: any) => void;
	uploadHeaderImageApi?: (image: any) => void;
	uploadBannerImageApi?: (image: any) => void;
	selectWebsiteDetails?: any;
	setSitesBayWebsiteDetails?: (details: any) => void;
	selectSitesBayWebsiteDetails?: any;
	shouldShowLaunchpad?: boolean;
	setShouldShowLaunchpad?: (show: boolean) => void;
	appSettings?: Record<string, any>;
	// Omitted -> BayUserTour falls back to window.BAY_USERTOUR
	userTour?: UserTourConfig;
}

// The parent element...
const TopNav = (props: TopNavProps) => {
	const [notificationsArray, setNotificationsArray] = useState<NotificationData[]>([]);
	const [quickLinksDataObject, setQuickLinksDataObject] = useState<Record<string, any>>({});
	const [showLaunchpadModal, setShowLaunchpadModal] = useState(false);
	const [showActAsModal, setShowActAsModal] = useState(false);

	const {
		t,
		user,
		appOrigin,
		sharedAppOrigin,
		fetchNotificationsFunc,
		fetchQuickLinksFunc,
		showNotifications = true,
		notificationsData,
		quickLinksData,
		bodyId = '#bayBody',
		withBanners = 'none',
		reqAccess,
		platformType,
		settingsOptions,
		showFeatureButton,
		featureList,
		setFeatureFunc,
		children,
		productStages,
		openMenuDrawer,
		setIsDrawerOpen = () => {},
		enableTestimonialTree = false,
		quickLinkService,
		showSupportButton,
		currentLanguage,
		onLanguageChange,
		supportedLanguages,
		weglotReady,
		enableTranslations = false,
		showLaunchpadButton = false,
		showBrandLogo = false,
		showLaunchpadUpgradeButton = false,
		showAnnouncementBanners = false,
		menuType,
		setMenuType,
		showOffCanvas,
		setShowOffCanvas,
		toggleDrawer,
		updateNavSettings,
		showApiTokenButton,
		onLogout,
		isMobileView,
		navSettings,
		readLaunchpadApi = () => {},
		updateLaunchpadApi = () => {},
		fetchWebsiteDetailsApi = () => {},
		selectLaunchpadSettingsData = () => {},
		selectLaunchpadStatus = () => {},
		selectUserProductAccess = () => {},
		setLaunchpadSettingsData = () => {},
		setUserProductAccess = () => {},
		setWebsiteDetails = () => {},
		uploadHeaderImageApi = () => {},
		uploadBannerImageApi = () => {},
		selectWebsiteDetails = () => {},
		setSitesBayWebsiteDetails = () => {},
		selectSitesBayWebsiteDetails = () => {},
		shouldShowLaunchpad,
		setShouldShowLaunchpad,
		appSettings,
		userTour,
	} = props;

	const navRef = useRef(null);

	const queryParams = new URLSearchParams(window.location.search);

	useEffect(() => {
		checkNotifications();
	}, [user]);

	useEffect(() => {
		addBodyPadding(withBanners);
	}, [withBanners, platformType]);

	// Publish the nav's live bottom edge as --bay-nav-bottom, consumed by
	// BayDrawer.scss so the notifications / My Account / Product Updates drawers
	// start where the nav actually ends rather than at a frozen offset. Same idea
	// as ActAsBanner's --actas-banner-height, and it composes with it: the nav's
	// bottom is its own height plus the banner-driven margin-top, and those move
	// independently, so no fixed number tracks it.
	//
	// Three things move that edge, so all three are watched:
	//   ResizeObserver     the nav's own height (e.g. smc's balance-due state)
	//   MutationObserver   --actas-banner-height, which ActAsBanner writes to
	//                      body.style — it changes the margin-top without changing
	//                      the nav's size, so ResizeObserver never sees it
	//   resize/orientation viewport crossing the 576/768px media queries
	useLayoutEffect(() => {
		const el = navRef.current;
		if (!el) return undefined;

		// Written to the ROOT, not `body`, because the MutationObserver below
		// watches `body`'s style attribute — writing to the observed node would
		// retrigger the observer and spin. Custom properties inherit, so a portaled
		// backdrop still sees it. The equality guard is a second line of defence.
		const publishBottom = () => {
			// The nav is position: fixed, so `bottom` is already viewport-relative.
			const next = `${el.getBoundingClientRect().bottom}px`;
			const root = document.documentElement;
			if (root.style.getPropertyValue('--bay-nav-bottom') === next) return;
			root.style.setProperty('--bay-nav-bottom', next);
		};

		publishBottom();

		const observers = [];
		if (typeof ResizeObserver !== 'undefined') {
			const ro = new ResizeObserver(publishBottom);
			ro.observe(el);
			observers.push(ro);
		}
		if (typeof MutationObserver !== 'undefined') {
			const mo = new MutationObserver(publishBottom);
			mo.observe(document.body, {
				attributes: true,
				attributeFilter: ['style'],
			});
			// --usertour-banner-height is written to the root (see BayUserTour); it moves
			// the nav's `top` without resizing it. publishBottom's equality guard stops
			// the --bay-nav-bottom write below from looping on this observer.
			mo.observe(document.documentElement, {
				attributes: true,
				attributeFilter: ['style'],
			});
			observers.push(mo);
		}

		window.addEventListener('resize', publishBottom);
		window.addEventListener('orientationchange', publishBottom);

		return () => {
			observers.forEach((o) => o.disconnect());
			window.removeEventListener('resize', publishBottom);
			window.removeEventListener('orientationchange', publishBottom);
			document.documentElement.style.removeProperty('--bay-nav-bottom');
		};
		// withBanners changes which margin-top rule applies, so re-measure on it.
	}, [withBanners, platformType]);

	useEffect(() => {
		if (openMenuDrawer) {
			toggleDrawer(openMenuDrawer, true);
		}
	}, [openMenuDrawer]);

	useEffect(() => {
		setNotificationsArray(notificationsData);
		setQuickLinksDataObject(quickLinksData);
	}, [notificationsData, quickLinksData]);

	useEffect(() => {
		if (shouldShowLaunchpad) {
			setShowLaunchpadModal(true);
			if (setShouldShowLaunchpad) {
				setShouldShowLaunchpad(false);
			}
		}
	}, [shouldShowLaunchpad]);

	useEffect(() => {
		const handleHashChange = () => {
			// Always open launchpad if hash is exactly '#launchpad' and modal is not already showing
			if (window.location.hash === '#launchpad' && !showLaunchpadModal) {
				setShowLaunchpadModal(true);
				setIsDrawerOpen(true);
			}
		};

		// Check on initial mount for non-social platforms
		if (platformType !== 'social') {
			const pageParam = queryParams.get('page');
			if (pageParam && pageParam.includes('#launchpad')) {
				queryParams.set('page', pageParam.split('#')[0]);
				window.history.replaceState(
					{},
					'',
					`${window.location.pathname}?${queryParams.toString()}`,
				);
			}
		}

		// Check if we should show launchpad on initial load
		handleHashChange();

		// Listen for hash changes
		window.addEventListener('hashchange', handleHashChange);

		// Cleanup event listener
		return () => {
			window.removeEventListener('hashchange', handleHashChange);
		};
	}, [showLaunchpadModal]);

	const checkNotifications = () => {
		// https://stackoverflow.com/questions/38114266/notification-requestpermission-throws-an-error-in-mac-versions-of-safari
		// https://stackoverflow.com/questions/38422340/check-if-browser-notification-is-available
		try {
			if ('Notification' in window) {
				// API supported
				if (window.Notification?.permission !== 'granted') {
					window.Notification.requestPermission();
				}
			} else {
				// API not supported
				console.log('"Notification" not supported in the browser window.');
			}
		} catch (error) {
			// Safari doesn't return a promise for requestPermissions and it
			// throws a TypeError. It takes a callback as the first argument
			// instead.
			console.log(
				'"Notification" not supported in the browser window. Error:',
				error,
			);
		}
	};

	// TODO: Refactor along with the individual platform and component css
	// Also remove the jquery dependency
	// NOTE: Double banners only really apply to Social currently
	// will be refactored away in the future
	const addBodyPadding = (styleSize) => {
		let bodyIdString = bodyId;
		if (bodyId.substring(0, 1) !== '#') {
			bodyIdString = `#${bodyIdString}`;
		}

		if (styleSize === 'single' && platformType === 'client') {
			$(`${bodyIdString}`).addClass('clientBannerSingleMobile');
		} else {
			$(`${bodyIdString}`).removeClass('clientBannerSingleMobile');
		}

		if (styleSize === 'single') {
			$(`${bodyIdString}`).addClass('banner-single');
			$('#BaySideNav').addClass('banner-single');
			document.body.classList.add('actingas');
		} else if (styleSize === 'double') {
			$(`${bodyIdString}`).addClass('banner-double');
			$('#BaySideNav').addClass('banner-double');
			document.body.classList.add('actingas');
			document.body.classList.add('freetrial');
		} else {
			$(`${bodyIdString}`).removeClass('banner-single');
			$(`${bodyIdString}`).removeClass('banner-double');
			$('#BaySideNav').removeClass('banner-single');
			$('#BaySideNav').removeClass('banner-double');
			document.body.classList.remove('actingas');
			document.body.classList.remove('amountdue');
			document.body.classList.remove('freetrial');
		}
	};

	const handleClose = useCallback(() => {
		setShowLaunchpadModal(false);
		setIsDrawerOpen(false);

		// Clear the launchpad hash if it's currently in the URL
		if (window.location.hash === '#launchpad') {
			window.history.replaceState(
				null,
				null,
				window.location.pathname + window.location.search,
			);
		} else if (platformType !== 'social' && queryParams.get('page')) {
			queryParams.set('page', `${queryParams.get('page').split('#')[0]}`);
			window.history.pushState(
				{},
				'',
				`${window.location.pathname}?${queryParams.toString()}`,
			);
		}
	}, [queryParams, platformType]);

	const isSettingsDrawerOpen = showOffCanvas && menuType === 'settings';

	if(user?.billingStatus === 0) {
		user.outstandingBalAlert = 'pastdue';
		user.outstandingBalPayNowLink = true;
	}

	// Helper function to determine if balance due banner should be shown
	const shouldShowBalanceDueBanner = () => {
		if (platformType === 'client') {
			return !isActingAsAnotherUser(user) && user?.outstandingBalAmt?.length > 0;
		} else {
			return user?.billingStatus === 0;
		}
	};

	// The purple "from InternalBAY" bar is the social launch banner (social +
	// no actor account). It appears whenever a genuine InternalBAY launch is
	// active, which the backend flags with ActingAsSource === 'internal' (set at
	// launch time in SiteController). Internal staff can launch into ANY account,
	// enterprise or not, so this must NOT be gated on the account's own Act As
	// capability (isEnterpriseParent/showActAsButton) — that gate wrongly hid the
	// bar for non-enterprise accounts. An enterprise parent acting as one of its
	// own children is a different flow: it sets ActingAsActorAccount, which lands
	// on the yellow "changed user" bar, not this launch bar. A direct login has
	// an empty ActingAsSource (and isActingAsAnotherUser already rejects a
	// lingering/self ActingAsPUBLICID), so no false banner appears.
	const isSocialLaunchBanner =
		platformType === 'social' && !user?.ActingAsActorAccount;
	const canShowActAsBanner =
		isActingAsAnotherUser(user) &&
		(!isSocialLaunchBanner || user?.ActingAsSource === 'internal');
	// A Back At You global admin has no account of their own here: until they
	// pick a user to act as, the Act As modal is up and cannot be dismissed.
	//
	// The dismissal has to be recorded HERE, not in the modal. mustActAs is derived
	// from the user prop alone, so the modal closing itself does nothing -- this
	// component re-asserts showModal on the very next render and the admin is back
	// in the same dialog. The modal only lets itself be dismissed when its gate is
	// unsatisfiable (list failed to load, nobody to pick); this records that the
	// admin took that exit, so the gate stays down until the page reloads.
	const [gateDismissed, setGateDismissed] = useState(false);
	const mustActAs =
		Boolean(user?.IsGlobalAdmin) &&
		!isActingAsAnotherUser(user) &&
		!gateDismissed;

	return (
		<>
			<BayUserTour config={userTour} />
			{withBanners !== 'none' && canShowActAsBanner ? (
				<ActAsBanner
					actingAsSource={user?.ActingAsSource}
					actorAccount={user?.ActingAsActorAccount}
					openActAsModal={setShowActAsModal}
					t={t}
					platform={platformType}
					userFirstName={user?.ActingAsFIRSTNAME}
					userLastName={user?.ActingAsLASTNAME}
					userPublicId={user?.PUBLICID}
				/>
			) : null}
			{withBanners !== 'none' && user?.isFreeTrial ? (
				<FreeTrialBanner
					bannerValue={withBanners}
					daysLeft={user?.daysLeft}
					expired={user?.expiredFreeTrial}
					platform={platformType}
				/>
			) : null}
			<BayNav
				ref={navRef}
				$platformType={platformType}
				$actorAccount={user?.ActingAsActorAccount}
				className={`bay-nav border-bottom ${withBanners !== 'none' ? `banner-${withBanners}` : ''}`}
			>
				<DesktopBranding
					platformType={platformType}
					setIsDrawerOpen={setIsDrawerOpen}
					setShowLaunchpadModal={setShowLaunchpadModal}
					showBrandLogo={showBrandLogo}
					showLaunchpadButton={showLaunchpadButton}
					showLaunchpadModal={showLaunchpadModal}
					showLaunchpadUpgradeButton={showLaunchpadUpgradeButton}
					t={t}
				/>
				<ActionNav className="action-nav">
					{children ? (
						<>{children}</>
					) : (
						<>
							<div className="d-flex flex-grow-1 justify-content-center align-items-center">
								<div className="inner-banner-wrapper w-100 d-none d-md-flex justify-content-center align-items-center">
									{shouldShowBalanceDueBanner() && (
										<BalanceDueBanner
											actingAsPublicId={user?.ActingAsPUBLICID}
											outstandingBalAlert={user?.outstandingBalAlert}
											outstandingBalAmt={user?.outstandingBalAmt}
											outstandingBalPayNowLink={
												user?.outstandingBalPayNowLink
											}
											platform={platformType}
											t={t}
										/>
									)}
								</div>
							</div>

							{showNotifications && (
								<NotifyButton
									itemCount={notificationsArray?.length}
									toggleFunc={() => toggleDrawer('notifications')}
								/>
							)}

							{showLaunchpadButton && (
								<LaunchpadButtonWrapper className="d-block d-md-none me-2">
									<LaunchpadButton
										disabled={showLaunchpadUpgradeButton}
										platformType={platformType}
										setIsDrawerOpen={setIsDrawerOpen}
										setShowLaunchpadModal={setShowLaunchpadModal}
										showLaunchpadModal={showLaunchpadModal}
										t={t}
									/>
								</LaunchpadButtonWrapper>
							)}

							{platformType !== 'social' &&
								Object?.keys(quickLinksDataObject)?.length > 0 && (
								<QuickLinksButton toggleFunc={() => toggleDrawer('links')} />
							)}

							{(platformType === 'social' || (platformType === 'client' && user?.ActingAsACCESSLEVEL !== 'U')) && (
								<SettingsButton
									isActive={isSettingsDrawerOpen}
									toggleFunc={() => toggleDrawer('settings')}
								/>
							)}

							<DividerDiv
								className="bay-nav-divider desktop-menu-icons"
								showDivider
							/>

							{showFeatureButton && (
								<FeatureMenuButton toggleFunc={() => toggleDrawer('feature')} />
							)}

							{showSupportButton && (
								<SupportButton platform={platformType} type="chat" />
							)}

							<SupportButton type="help" />

							<MyAccountButton
								toggleFunc={() => toggleDrawer('user')}
								user={user}
								showActAsModal={setShowActAsModal}
								platform={platformType}
								t={t}
							/>
						</>
					)}
				</ActionNav>

				<div className="w-100 flex-grow-1 d-flex d-md-none justify-content-center inner-banner-wrapper justify-content-center align-items-center">
					{shouldShowBalanceDueBanner() && (
						<BalanceDueBanner
							actingAsPublicId={user?.ActingAsPUBLICID}
							outstandingBalAlert={user?.outstandingBalAlert}
							outstandingBalAmt={user?.outstandingBalAmt}
							outstandingBalPayNowLink={user?.outstandingBalPayNowLink}
							platform={platformType}
							t={t}
						/>
					)}
				</div>
			</BayNav>

			{showLaunchpadButton && (<BayLaunchpad
				appOrigin={appOrigin}
				bannerType={withBanners}
				fetchWebsiteDetailsApi={fetchWebsiteDetailsApi}
				handleClose={handleClose}
				platformType={platformType}
				readLaunchpadApi={readLaunchpadApi}
				selectLaunchpadSettingsData={selectLaunchpadSettingsData}
				selectLaunchpadStatus={selectLaunchpadStatus}
				selectSitesBayWebsiteDetails={selectSitesBayWebsiteDetails}
				selectUserProductAccess={selectUserProductAccess}
				selectWebsiteDetails={selectWebsiteDetails}
				setLaunchpadSettingsData={setLaunchpadSettingsData}
				setShowLaunchpadModal={setShowLaunchpadModal}
				setSitesBayWebsiteDetails={setSitesBayWebsiteDetails}
				setUserProductAccess={setUserProductAccess}
				setWebsiteDetails={setWebsiteDetails}
				sharedAppOrigin={sharedAppOrigin}
				showLaunchpadModal={showLaunchpadModal || shouldShowLaunchpad}
				t={t}
				updateLaunchpadApi={updateLaunchpadApi}
				uploadBannerImageApi={uploadBannerImageApi}
				uploadHeaderImageApi={uploadHeaderImageApi}
				user={user}
			/>
			)}

			{(showActAsModal || mustActAs) && (
				<ActAsModal
					platform={platformType}
					required={mustActAs}
					// Host first: bay-ui components take what they need as props rather
					// than reaching for an app's mechanism (CLAUDE.md, "dumb
					// components"). The built-in is only a fallback for the two
					// platforms whose logout path lives in this library already, so no
					// host is left with a dismissal-free gate and no exit.
					onLogout={
						onLogout ??
						(canLogOff(platformType) ? () => logOff(platformType) : undefined)
					}
					showModal={showActAsModal || mustActAs}
					t={t}
					user={user}
					onHideFunc={() => {
						setShowActAsModal(false);

						// Only when the modal was serving the gate. A global admin who
						// opens Act As from the menu and cancels has not dismissed
						// anything, and latching there would disarm the forced gate.
						if (mustActAs) {
							setGateDismissed(true);
						}
					}}
				/>
			)}

			<BayNavDrawer
				alertAudio={navSettings?.alertAudio}
				currentLanguage={currentLanguage}
				enableTestimonialTree={enableTestimonialTree}
				enableTranslations={enableTranslations}
				featureList={featureList}
				fetchNotificationsFunc={fetchNotificationsFunc}
				fetchQuickLinksFunc={fetchQuickLinksFunc}
				isMobileView={isMobileView}
				appSettings={appSettings}
				menuType={menuType}
				notificationsArray={notificationsArray}
				platformType={platformType}
				productStages={productStages}
				quickLinksDataObject={quickLinksDataObject}
				quickLinkService={quickLinkService}
				reqAccess={reqAccess}
				selectLaunchpadSettingsData={selectLaunchpadSettingsData}
				setFeatureFunc={setFeatureFunc}
				setIsDrawerOpen={setIsDrawerOpen}
				setMenuType={setMenuType}
				setNotificationsArray={setNotificationsArray}
				setShowOffCanvas={setShowOffCanvas}
				settingsOptions={settingsOptions}
				showAnnouncementBanners={showAnnouncementBanners}
				showApiTokenButton={showApiTokenButton}
				showFeatureButton={showFeatureButton}
				showLaunchpadButton={showLaunchpadButton}
				showLaunchpadUpgradeButton={showLaunchpadUpgradeButton}
				showOffCanvas={showOffCanvas}
				supportedLanguages={supportedLanguages}
				t={t}
				updateLaunchpadApi={updateLaunchpadApi}
				updateNavSettings={updateNavSettings}
				user={user}
				weglotReady={weglotReady}
				withBanners={withBanners}
				onLanguageChange={onLanguageChange}
			/>
		</>
	);
};

export default TopNav;
